// /pages/SmiTestPgmReportPage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging, ColumnChooser
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const SMI_TEST_PGM_API_URL = `${API_URL}/api/oracle/smi-test-pgm-report`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

export default function SmiTestPgmReportPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const dataGridRef = useRef(null);

  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}`;
  };

 
  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } }).then(res => setCustList(res.data.data || []));
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) { setDeviceList([]); setSelectedDevice('ALL'); return; }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } })
      .then(res => {
        const fetchedDevices = res.data.data || [];
        setDeviceList([{ CODE_CD: 'ALL' }, ...fetchedDevices]);
        setSelectedDevice('ALL');
      });
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  
  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      startDate: formatDate(dateFrom),
      endDate: formatDate(dateTo),
      custCd: selectedCust,
      device: selectedDevice,
    };
    
    axios.get(SMI_TEST_PGM_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, dateFrom, dateTo, selectedCust, selectedDevice]);
  
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SMI_Test_PGM_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "SMI_Test_PGM_Report.xlsx"));
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>SMI Test PGM Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} />
    
          {/* ★★★ [수정] 컬럼 정의 (컬럼 밴딩 사용) ★★★ */}
          {/* 기본 정보 */}
          <Column dataField="SO_NO" caption="SO No." width={120} fixed={true} />
          <Column dataField="MAT_ID" caption="Device" width={150} fixed={true} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} fixed={true} />
          <Column dataField="MVT" caption="MVT" width={100} />
          <Column dataField="OUT_QTY" caption="Out Qty" dataType="number" alignment="right" width={100} />
          <Column dataField="SHIP_DATE" caption="Ship Date" width={180} />
          <Column dataField="SHIP_TO" caption="Ship To" width={120} />

          {/* Sub Lot 1 정보 (밴드 컬럼) */}
          <Column caption="Sub Lot 1 Info">
            <Column dataField="SUB_LOT_ID1" caption="Sub Lot ID" width={150} />
            <Column dataField="SUB_LOT_QTY1" caption="Sub Lot Qty" dataType="number" alignment="right" width={120} />
            <Column dataField="SUB_LOT_1_HOT" caption="Hot PGM" width={200} />
            <Column dataField="SUB_LOT_1_HOT_TESTER" caption="Hot Tester" width={120} />
            <Column dataField="SUB_LOT_1_ROOM" caption="Room PGM" width={200} />
            <Column dataField="SUB_LOT_1_ROOM_TESTER" caption="Room Tester" width={120} />
            <Column dataField="SUB_LOT_1_COLD" caption="Cold PGM" width={200} />
            <Column dataField="SUB_LOT_1_COLD_TESTER" caption="Cold Tester" width={120} />
            <Column dataField="SUB_LOT_1_ROOM2" caption="Room2 PGM" width={200} />
            <Column dataField="SUB_LOT_1_ROOM2_TESTER" caption="Room2 Tester" width={120} />
            <Column dataField="SUB_LOT_1_EQA" caption="EQA PGM" width={200} />
            <Column dataField="SUB_LOT_1_EQA_TESTER" caption="EQA Tester" width={120} />
          </Column>

          {/* Sub Lot 2 정보 (밴드 컬럼) */}
          <Column caption="Sub Lot 2 Info">
            <Column dataField="SUB_LOT_ID2" caption="Sub Lot ID" width={150} />
            <Column dataField="SUB_LOT_QTY2" caption="Sub Lot Qty" dataType="number" alignment="right" width={120} />
            <Column dataField="SUB_LOT_2_HOT" caption="Hot PGM" width={200} />
            <Column dataField="SUB_LOT_2_HOT_TESTER" caption="Hot Tester" width={120} />
            <Column dataField="SUB_LOT_2_ROOM" caption="Room PGM" width={200} />
            <Column dataField="SUB_LOT_2_ROOM_TESTER" caption="Room Tester" width={120} />
            <Column dataField="SUB_LOT_2_COLD" caption="Cold PGM" width={200} />
            <Column dataField="SUB_LOT_2_COLD_TESTER" caption="Cold Tester" width={120} />
            <Column dataField="SUB_LOT_2_ROOM2" caption="Room2 PGM" width={200} />
            <Column dataField="SUB_LOT_2_ROOM2_TESTER" caption="Room2 Tester" width={120} />
            <Column dataField="SUB_LOT_2_EQA" caption="EQA PGM" width={200} />
            <Column dataField="SUB_LOT_2_EQA_TESTER" caption="EQA Tester" width={120} />
          </Column>

        </DataGrid>
      </div>
    </div>
  );
}